(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const initHeader = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const body = document.body;
    const shopToggle = root.querySelector('[data-shop-toggle]');
    const shopMenu = root.querySelector('[data-shop-menu]');
    const shopBackdrop = root.querySelector('[data-shop-backdrop]');
    const panelBackdrop = root.querySelector('[data-panel-backdrop]');
    const panels = [...root.querySelectorAll('[data-panel]')];
    const searchExperience = root.querySelector('[data-search-experience]');
    const searchForm = searchExperience?.querySelector('[data-search-form]');
    const searchInput = searchExperience?.querySelector('[data-search-input]');
    const searchClear = searchExperience?.querySelector('[data-search-clear]');
    const searchResults = searchExperience?.querySelector('[data-search-results]');
    const defaultSearchMarkup = searchResults?.innerHTML || '';
    let searchIndex = [];
    let searchSequence = 0;
    let searchTimer;
    try {
      searchIndex = JSON.parse(root.querySelector('[data-siwa-search-index]')?.textContent || '[]');
    } catch (_) {
      searchIndex = [];
    }
    const indexNode = root.querySelector('[data-siwa-search-index]');
    let indexPromise;
    const ensureSearchIndex = () => {
      const total = Number(indexNode?.dataset.searchIndexTotal || 0);
      if (searchIndex.length >= total) {
        if (indexNode) indexNode.dataset.ready = 'true';
        return Promise.resolve();
      }
      if (indexPromise) return indexPromise;
      // Collection pagination must not paginate the header's search index.
      indexPromise = fetch(indexNode.dataset.searchIndexUrl, { headers: { Accept: 'text/html' } })
        .then(response => { if (!response.ok) throw new Error('Search index unavailable'); return response.text(); })
        .then(html => {
          const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-siwa-search-index]');
          const products = JSON.parse(replacement?.textContent || '[]');
          if (products.length < total) throw new Error('Incomplete search index');
          searchIndex = products;
          indexNode.textContent = JSON.stringify(products);
          indexNode.dataset.ready = 'true';
        }).catch(error => { indexPromise = null; throw error; });
      return indexPromise;
    };
    ensureSearchIndex().catch(() => {});
    let returnFocus = null;

    const syncBodyLock = () => {
      const hasOpenPanel = panels.some((panel) => panel.classList.contains('is-open'));
      const hasOpenMenu = shopMenu?.classList.contains('is-open');
      body.classList.toggle('is-locked', Boolean(hasOpenPanel || hasOpenMenu));
    };

    const setShopMenu = (open) => {
      shopMenu?.classList.toggle('is-open', open);
      shopMenu?.setAttribute('aria-hidden', String(!open));
      shopBackdrop?.classList.toggle('is-open', open);
      shopToggle?.setAttribute('aria-expanded', String(open));
      syncBodyLock();
    };

    const closePanels = ({ restoreFocus = true } = {}) => {
      panels.forEach((panel) => {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
      });
      root.querySelectorAll('[data-panel-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      if (panelBackdrop) panelBackdrop.hidden = true;
      syncBodyLock();
      if (restoreFocus && returnFocus instanceof HTMLElement) returnFocus.focus();
      returnFocus = null;
    };

    const openPanel = (name, trigger) => {
      closePanels({ restoreFocus: false });
      setShopMenu(false);
      const panel = panels.find((candidate) => candidate.dataset.panel === name);
      if (!panel) return;
      panel.querySelectorAll('img[data-deferred-src]').forEach((image) => {
        image.src = image.dataset.deferredSrc;
        image.removeAttribute('data-deferred-src');
      });
      panel.scrollTop = 0;
      if (name === 'mobile') {
        panel.querySelectorAll('[data-mobile-nav-toggle]').forEach((toggle) => {
          const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
          toggle.setAttribute('aria-expanded', 'false');
          if (submenu) submenu.hidden = true;
        });
      }
      returnFocus = trigger || document.activeElement;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      if (panelBackdrop) panelBackdrop.hidden = false;
      trigger?.setAttribute('aria-expanded', 'true');
      syncBodyLock();
      const useSearchField = name === 'search' && !window.matchMedia('(max-width: 989px)').matches;
      const preferredFocus = useSearchField
        ? panel.querySelector('[data-search-input]')
        : panel.querySelector('[data-panel-close], input, a[href], button');
      let focusFrames = 0;
      const focusOpenedPanel = () => {
        if (!panel.classList.contains('is-open')) return;
        // Opening from a page CTA can coincide with the visibility transition.
        // Wait for a rendered panel before transferring keyboard focus into it.
        if (window.getComputedStyle(panel).visibility === 'visible') {
          (preferredFocus || panel).focus({ preventScroll: true });
          if (!panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
        }
        if (!panel.contains(document.activeElement) && focusFrames++ < 12) {
          requestAnimationFrame(focusOpenedPanel);
        }
      };
      requestAnimationFrame(focusOpenedPanel);
    };
    root.siwaOpenPanel = openPanel;

    shopToggle?.addEventListener('click', () => {
      setShopMenu(!shopMenu?.classList.contains('is-open'));
    });
    shopBackdrop?.addEventListener('click', () => setShopMenu(false));
    root.querySelector('[data-shop-close]')?.addEventListener('click', () => {
      setShopMenu(false);
      shopToggle?.focus();
    });

    root.querySelectorAll('[data-panel-open]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        openPanel(trigger.dataset.panelOpen, trigger);
      });
    });
    root.querySelectorAll('[data-panel-close]').forEach((trigger) => {
      trigger.addEventListener('click', () => closePanels());
    });
    panelBackdrop?.addEventListener('click', () => closePanels());

    document.addEventListener('siwa:cart:open', () => {
      const cartTrigger = root.querySelector('[data-panel-open="cart"]');
      const focusOrigin = document.activeElement;
      openPanel('cart', cartTrigger);
      if (focusOrigin instanceof HTMLElement && focusOrigin !== cartTrigger) returnFocus = focusOrigin;
    });

    root.querySelectorAll('[data-panel-switch="search"]').forEach((switcher) => {
      switcher.addEventListener('click', () => {
        const searchTrigger = root.querySelector('[data-panel-open="search"]');
        openPanel('search', searchTrigger || switcher);
      });
    });

    const syncSearchState = () => {
      if (!searchInput || !searchClear) return;
      searchClear.hidden = searchInput.value.trim().length === 0;
      searchForm?.classList.toggle('has-value', !searchClear.hidden);
    };

    const smartSearch = (query) => window.SiwaSearch.search(searchIndex, query);

    const escapeSearchHtml = (value) => {
      const container = document.createElement('div');
      container.textContent = value == null ? '' : String(value);
      return container.innerHTML;
    };

    const searchResultLimit = () => 10;

    const renderSearchResults = (query, products) => {
      if (!searchResults) return;
      searchResults.removeAttribute('aria-busy');
      searchResults.classList.add('is-predictive');
      const visibleProducts = products.slice(0, searchResultLimit());
      if (!visibleProducts.length) {
        searchResults.innerHTML = `<p class="siwa-search__empty" role="status">No matching Siwa scents for “${escapeSearchHtml(query)}”.</p>`;
        return;
      }
      const productMarkup = visibleProducts.map((product) => {
        const media = product.image
          ? `<img src="${escapeSearchHtml(product.image)}" alt="" loading="lazy">`
          : '<span>SIWA</span>';
        return `<a class="siwa-search__result" href="${escapeSearchHtml(product.url || '#')}"><span class="siwa-search__result-media">${media}</span><span class="siwa-search__result-copy"><strong>${escapeSearchHtml(product.title)}</strong>${product.context ? `<small>${escapeSearchHtml(product.context)}</small>` : ''}${product.price ? `<span>${escapeSearchHtml(product.price)}</span>` : ''}</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13M14 7l5 5-5 5"/></svg></a>`;
      }).join('');
      const label = `${visibleProducts.length} ${visibleProducts.length === 1 ? 'match' : 'matches'} for “${escapeSearchHtml(query)}”`;
      searchResults.innerHTML = `<section class="siwa-search__smart" aria-live="polite"><header><span>FROM THE SIWA INDEX</span><strong>${label}</strong></header><div class="siwa-search__result-grid">${productMarkup}</div></section>`;
    };

    const resetSearchResults = () => {
      window.clearTimeout(searchTimer);
      searchSequence += 1;
      if (!searchResults) return;
      searchResults.classList.remove('is-predictive');
      searchResults.removeAttribute('aria-busy');
      searchResults.innerHTML = defaultSearchMarkup;
    };

    const predictSearch = async (query) => {
      if (!searchResults) return;
      const cleanQuery = query.trim();
      const sequence = ++searchSequence;
      if (cleanQuery.length < 3) {
        searchResults.classList.remove('is-predictive');
        searchResults.removeAttribute('aria-busy');
        searchResults.innerHTML = defaultSearchMarkup;
        return;
      }

      searchResults.classList.add('is-predictive');
      searchResults.setAttribute('aria-busy', 'true');
      try {
        await ensureSearchIndex();
        const localProducts = smartSearch(cleanQuery);
        if (sequence === searchSequence) renderSearchResults(cleanQuery, localProducts);
      } catch (_) {
        if (sequence === searchSequence) {
          searchResults.removeAttribute('aria-busy');
          searchResults.innerHTML = '<p role="status">Search suggestions could not load. Press Search to see results.</p>';
        }
      }
    };

    searchInput?.addEventListener('input', () => {
      syncSearchState();
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => predictSearch(searchInput.value), 180);
    });

    searchForm?.addEventListener('reset', () => {
      window.setTimeout(() => {
        syncSearchState();
        resetSearchResults();
        searchInput?.focus();
      }, 0);
    });

    syncSearchState();

    root.querySelectorAll('[data-mobile-nav-toggle]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const submenu = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!submenu) return;
        const open = submenu.hidden;
        if (open) {
          const accordion = trigger.closest('[data-mobile-nav-accordion]');
          accordion?.querySelectorAll('[data-mobile-nav-toggle][aria-expanded="true"]').forEach((otherTrigger) => {
            if (otherTrigger === trigger || otherTrigger.closest('[data-mobile-nav-accordion]') !== accordion) return;
            const otherSubmenu = document.getElementById(otherTrigger.getAttribute('aria-controls'));
            otherTrigger.setAttribute('aria-expanded', 'false');
            if (otherSubmenu) otherSubmenu.hidden = true;
          });
        }
        submenu.hidden = !open;
        trigger.setAttribute('aria-expanded', String(open));
      });
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (panels.some((panel) => panel.classList.contains('is-open'))) closePanels();
        else if (shopMenu?.classList.contains('is-open')) {
          setShopMenu(false);
          shopToggle?.focus();
        }
        return;
      }

      if (event.key !== 'Tab') return;
      const panel = panels.find((candidate) => candidate.classList.contains('is-open'));
      if (!panel) return;
      const focusable = [...panel.querySelectorAll(focusableSelector)].filter((element) => {
        if (element.hasAttribute('hidden') || element.closest('[hidden]')) return false;
        return element.getClientRects().length > 0;
      });
      if (!focusable.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initAnnouncement = (bar) => {
    if (!bar || bar.dataset.initialized === 'true') return;
    bar.dataset.initialized = 'true';
    const slides = [...bar.querySelectorAll('[data-announcement-slide]')];
    const applyPaperColor = (slide) => {
      const paperColor = slide?.dataset.announcementPaperColor;
      if (paperColor) bar.style.setProperty('--announcement-paper-color', paperColor);
    };
    applyPaperColor(slides[0]);
    if (slides.length < 2) return;
    let index = 0;
    let cycleTimer;
    let transitionTimers = [];
    let transitioning = false;
    let isHovered = false;
    let hasFocus = false;
    let isUserPaused = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const wetVideo = bar.querySelector('[data-announcement-wet-video]');
    const holdDuration = Number(bar.dataset.interval) || 3000;
    const transitionDuration = Number(bar.dataset.transitionDuration) || 3150;
    const videoPlaybackRate = Number(bar.dataset.videoPlaybackRate) || 1.65;
    const swapDelay = Math.round(transitionDuration * .62);
    const leaveDelay = Math.max(0, swapDelay - 190);

    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      applyPaperColor(slides[index]);
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.remove('is-leaving');
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelector('a, button')?.setAttribute('tabindex', active ? '0' : '-1');
      });
    };

    const clearCycle = () => {
      window.clearTimeout(cycleTimer);
      cycleTimer = undefined;
    };

    const isPaused = () => isUserPaused || isHovered || hasFocus;

    const schedule = () => {
      clearCycle();
      if (bar.dataset.autoplay !== 'true' || reducedMotion || isPaused() || transitioning) return;
      cycleTimer = window.setTimeout(() => transitionTo(index + 1), holdDuration);
    };

    const transitionTo = (nextIndex) => {
      if (transitioning) return;
      const normalizedIndex = (nextIndex + slides.length) % slides.length;
      if (normalizedIndex === index) return;
      clearCycle();

      if (reducedMotion) {
        show(normalizedIndex);
        schedule();
        return;
      }

      transitioning = true;
      const outgoingSlide = slides[index];
      if (wetVideo) {
        wetVideo.pause();
        wetVideo.playbackRate = videoPlaybackRate;
        try {
          wetVideo.currentTime = 0;
        } catch (error) {
          // Metadata may still be loading on the first cycle.
        }
        wetVideo.play().catch(() => {});
      }
      bar.classList.remove('is-transitioning');
      void bar.offsetWidth;
      bar.classList.add('is-transitioning');

      transitionTimers.push(window.setTimeout(() => {
        outgoingSlide?.classList.add('is-leaving');
      }, leaveDelay));

      transitionTimers.push(window.setTimeout(() => {
        show(normalizedIndex);
      }, swapDelay));

      transitionTimers.push(window.setTimeout(() => {
        bar.classList.remove('is-transitioning');
        if (wetVideo) {
          wetVideo.pause();
          try {
            wetVideo.currentTime = 0;
          } catch (error) {
            // The dry poster remains visible if seeking is unavailable.
          }
        }
        transitionTimers = [];
        transitioning = false;
        schedule();
      }, transitionDuration));
    };

    bar.querySelector('[data-announcement-previous]')?.addEventListener('click', () => {
      transitionTo(index - 1);
    });
    bar.querySelector('[data-announcement-next]')?.addEventListener('click', () => {
      transitionTo(index + 1);
    });
    const pauseButton = bar.querySelector('[data-announcement-pause]');
    pauseButton?.addEventListener('click', () => {
      isUserPaused = !isUserPaused;
      pauseButton.dataset.paused = String(isUserPaused);
      pauseButton.setAttribute('aria-label', isUserPaused ? 'Play announcements' : 'Pause announcements');
      if (isUserPaused) clearCycle();
      else schedule();
    });
    bar.addEventListener('mouseenter', () => {
      isHovered = true;
      clearCycle();
    });
    bar.addEventListener('mouseleave', () => {
      isHovered = false;
      schedule();
    });
    bar.addEventListener('focusin', () => {
      hasFocus = true;
      clearCycle();
    });
    bar.addEventListener('focusout', (event) => {
      if (!bar.contains(event.relatedTarget)) {
        hasFocus = false;
        schedule();
      }
    });

    show(0);
    if (bar.dataset.autoplay === 'true' && !reducedMotion) {
      // Start the paper transition as soon as the first frame has painted. The
      // regular hold duration is restored after this opening transition.
      cycleTimer = window.setTimeout(() => transitionTo(1), 80);
    } else {
      schedule();
    }
  };

  const initHero = (hero) => {
    if (!hero || hero.dataset.initialized === 'true') return;
    hero.dataset.initialized = 'true';
    const videos = [...hero.querySelectorAll('[data-siwa-hero-video]')];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!videos.length) return;

    if (reducedMotion) {
      videos.forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
      return;
    }

    const playRenderedVideos = () => {
      videos.forEach((video) => {
        const isRendered = window.getComputedStyle(video).display !== 'none';
        if (isRendered) {
          video.preload = 'auto';
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    };

    // Autoplay is declared in the markup, but explicitly start the rendered
    // source during initialization so playback does not wait for the observer.
    playRenderedVideos();
    window.requestAnimationFrame(playRenderedVideos);

    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) playRenderedVideos();
        else videos.forEach((video) => video.pause());
      });
    }, { threshold: 0.08 });
    observer.observe(hero);
  };

  const initFeaturedCollection = (section) => {
    if (!section || section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';

    const track = section.querySelector('[data-collection-track]');
    const previousButton = section.querySelector('[data-collection-previous]');
    const nextButton = section.querySelector('[data-collection-next]');

    const updateArrowState = () => {
      if (!track) return;
      const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      if (previousButton) previousButton.disabled = track.scrollLeft <= 2;
      if (nextButton) nextButton.disabled = track.scrollLeft >= maximumScroll - 2;
    };

    const scrollCollection = (direction) => {
      if (!track) return;
      const firstCard = track.querySelector('.siwa-fc-card');
      const trackStyles = window.getComputedStyle(track);
      const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      const distance = (firstCard?.getBoundingClientRect().width || track.clientWidth) + gap;
      track.scrollBy({ left: distance * direction, behavior: 'smooth' });
    };

    previousButton?.addEventListener('click', () => scrollCollection(-1));
    nextButton?.addEventListener('click', () => scrollCollection(1));
    track?.addEventListener('scroll', updateArrowState, { passive: true });
    window.addEventListener('resize', updateArrowState, { passive: true });
    updateArrowState();
  };

  document.querySelectorAll('[data-header-root]').forEach(initHeader);
  document.querySelectorAll('[data-announcement]').forEach(initAnnouncement);
  document.querySelectorAll('[data-siwa-hero]').forEach(initHero);
  document.querySelectorAll('[data-siwa-featured-collection]').forEach(initFeaturedCollection);

  // The store follows lazy-loaded reviews. Keep this explicit destination in
  // place while those cards expand, without changing other anchors or taking
  // control back once the visitor starts scrolling or interacting.
  const initStoreAnchor = () => {
    const target = document.getElementById('siwa-store-visit');
    if (!target) return;
    const hash = '#siwa-store-visit';
    const reviews = document.querySelector('[data-siwa-reviews="home"]');
    let stop = () => {};
    const navigate = () => {
      stop();
      if (location.hash !== hash) return;
      const controller = new AbortController();
      let frame = 0;
      let settled;
      let deadline;
      const resize = new ResizeObserver(() => schedule());
      const mutation = new MutationObserver(() => schedule());
      stop = () => {
        controller.abort();
        resize.disconnect();
        mutation.disconnect();
        cancelAnimationFrame(frame);
        clearTimeout(settled);
        clearTimeout(deadline);
      };
      const align = () => {
        frame = 0;
        if (controller.signal.aborted) return;
        if (location.hash !== hash || !target.isConnected) return stop();
        const offset = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        if (Math.abs(target.getBoundingClientRect().top - offset) > 2) {
          target.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
        clearTimeout(settled);
        if (reviews?.dataset.state !== 'loading' && document.fonts?.status !== 'loading') {
          settled = setTimeout(stop, 1200);
        }
      };
      const schedule = () => {
        if (!controller.signal.aborted && !frame) frame = requestAnimationFrame(align);
      };
      for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
        window.addEventListener(type, stop, { capture: true, passive: true, signal: controller.signal });
      }
      window.addEventListener('pagehide', stop, { once: true, signal: controller.signal });
      window.addEventListener('load', schedule, { once: true, signal: controller.signal });
      resize.observe(document.querySelector('main') || document.body);
      if (reviews) mutation.observe(reviews, { attributes: true, attributeFilter: ['data-state'] });
      document.fonts?.ready.then(schedule);
      deadline = setTimeout(stop, 15000);
      align();
    };
    window.addEventListener('hashchange', navigate);
    document.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target || link.hasAttribute('download')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname || url.hash !== hash) return;
      event.preventDefault();
      // Retain locale/preview parameters when navigating within this page.
      if (location.hash !== hash) history.pushState(history.state, '', hash);
      navigate();
    });
    navigate();
  };
  initStoreAnchor();

  const syncNotesCard = (card, showNotes) => {
    const image = card.querySelector('[data-card-image]');
    if (!image) return;
    const selected = card.querySelector('[data-siwa-card-variant]:checked');
    const bottleUrl = selected?.dataset.imageUrl || image.dataset.cardBottleImageUrl || image.src;
    const notesUrl = selected?.dataset.notesImageUrl || image.dataset.cardNotesImageUrl || '';
    image.dataset.cardBottleImageUrl = bottleUrl;
    image.dataset.cardNotesImageUrl = notesUrl;
    image.src = showNotes && notesUrl ? notesUrl : bottleUrl;
    image.removeAttribute('srcset');
    card.dataset.showNotes = String(Boolean(showNotes && notesUrl));
  };

  const syncNotesScope = (scope, showNotes) => {
    if (!scope) return;
    scope.dataset.showNotes = String(showNotes);
    scope.querySelectorAll('[data-siwa-notes-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-pressed', String(showNotes));
    });
    scope.querySelectorAll('[data-siwa-product-card]').forEach((card) => syncNotesCard(card, showNotes));
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.('[data-siwa-notes-toggle]');
    if (!toggle) return;
    const scope = toggle.closest('[data-notes-view-scope]');
    const showNotes = toggle.getAttribute('aria-pressed') !== 'true';
    syncNotesScope(scope, showNotes);
  });

  document.addEventListener('siwa:notes-view:sync', (event) => {
    const scope = event.detail?.root;
    if (scope) syncNotesScope(scope, scope.dataset.showNotes === 'true');
  });

  document.addEventListener('change', (event) => {
    const variant = event.target.closest?.('[data-siwa-card-variant]');
    if (!variant || !variant.checked) return;

    const card = variant.closest('[data-siwa-product-card]');
    if (!card) return;

    const available = variant.dataset.available === 'true';
    const button = card.querySelector('.siwa-fc-card__add');
    const label = card.querySelector('[data-add-label]');
    const variantId = card.querySelector('[data-card-variant-id]');
    const price = card.querySelector('[data-card-price]');
    const image = card.querySelector('[data-card-image]');
    const format = card.querySelector('[data-card-format]');

    if (variantId) variantId.value = variant.value;
    if (price) price.textContent = variant.dataset.price || '';
    if (format) format.textContent = variant.dataset.variantTitle || '';
    if (button) {
      button.disabled = !available;
      button.dataset.available = String(available);
    }
    if (label) label.textContent = available ? 'Add to bag' : 'Sold out';
    if (image && variant.dataset.imageUrl) {
      image.dataset.cardBottleImageUrl = variant.dataset.imageUrl;
      image.dataset.cardNotesImageUrl = variant.dataset.notesImageUrl || '';
      const showNotes = card.closest('[data-notes-view-scope]')?.dataset.showNotes === 'true';
      image.src = showNotes && variant.dataset.notesImageUrl ? variant.dataset.notesImageUrl : variant.dataset.imageUrl;
      image.removeAttribute('srcset');
      card.dataset.showNotes = String(Boolean(showNotes && variant.dataset.notesImageUrl));
    }
    if (variant.dataset.variantUrl) {
      card.querySelectorAll('[data-card-link]').forEach((link) => {
        link.href = variant.dataset.variantUrl;
      });
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('[data-siwa-quick-add]');
    if (!form) return;

    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const label = form.querySelector('[data-add-label]');
    const status = form.querySelector('[data-add-status]');
    if (!button || button.disabled || button.classList.contains('is-loading')) return;

    const originalLabel = label?.textContent || 'Add to bag';
    button.disabled = true;
    button.classList.add('is-loading');
    if (label) label.textContent = 'Adding';
    if (status) status.textContent = '';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'Could not add this fragrance.');

      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      if (cart) {
        document.querySelectorAll('.siwa-header__count').forEach((count) => {
          count.textContent = cart.item_count;
        });
        document.querySelectorAll('.siwa-header__bag').forEach((bag) => {
          bag.setAttribute('aria-label', `Bag, ${cart.item_count} items`);
        });
      }
      document.dispatchEvent(new CustomEvent('siwa:cart:updated', {
        detail: { cart, line: payload, open: true, source: 'product-card' },
      }));

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      if (label) label.textContent = 'Added';
      window.setTimeout(() => {
        button.classList.remove('is-added');
        if (label?.isConnected) label.textContent = originalLabel;
      }, 1600);
    } catch (error) {
      if (label) label.textContent = originalLabel;
      if (status) status.textContent = error.message || 'Could not add this fragrance.';
    } finally {
      button.classList.remove('is-loading');
      button.disabled = button.dataset.available !== 'true';
    }
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-siwa-open-navigation]');
    if (!trigger) return;
    event.preventDefault();
    const requestedDelay = Number.parseInt(trigger.dataset.siwaMenuDelay, 10) || 0;
    const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : requestedDelay;
    if (!delay) {
      document.querySelector('[data-header-root]')?.siwaOpenPanel?.('mobile', trigger);
      return;
    }
    if (trigger.dataset.siwaMenuOpening === 'true') return;
    trigger.dataset.siwaMenuOpening = 'true';
    window.setTimeout(() => {
      delete trigger.dataset.siwaMenuOpening;
      document.querySelector('[data-header-root]')?.siwaOpenPanel?.('mobile', trigger);
    }, delay);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-managed-search-form]');
    if (!form) return;
    const query = form.elements.namedItem('q');
    if (!(query instanceof HTMLInputElement)) return;
    const constraint = 'tag:"Catalog: Siwa Master"';
    const cleanQuery = query.value
      .replace(/\s*tag:(?:"|&quot;)Catalog: Siwa Master(?:"|&quot;)\s*/gi, ' ')
      .trim();
    query.value = cleanQuery ? `${cleanQuery} ${constraint}` : constraint;
  }, true);

  // A product name in its own review list means "view this product", not
  // "reload here" (mobile browsers restore the old review scroll on reload).
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target || link.hasAttribute('download')) return;
    const target = new URL(link.href, location.href);
    if (target.origin !== location.origin || !target.pathname.includes('/products/') || target.pathname !== location.pathname || target.search !== location.search || target.hash) return;
    event.preventDefault();
    history.replaceState(history.state, '', target.href);
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.querySelector('[data-siwa-product] h1')?.focus({ preventScroll: true });
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-header-root]').forEach(initHeader);
    event.target.querySelectorAll('[data-announcement]').forEach(initAnnouncement);
    event.target.querySelectorAll('[data-siwa-hero]').forEach(initHero);
    event.target.querySelectorAll('[data-siwa-featured-collection]').forEach(initFeaturedCollection);
  });
})();
